<template>
    <Teleport to="body">
        <div
            v-if="visible"
            class="dv2-dock"
            :class="{
                'is-open': isOpen,
                'is-documento-preview': isDocumentoPreview,
            }"
        >
            <transition name="dv2-dock-panel">
                <section
                    v-if="isOpen"
                    class="dv2-dock__panel"
                    role="dialog"
                    aria-label="Seu assistente"
                    aria-modal="false"
                >
                    <header class="dv2-dock__head">
                        <div class="dv2-dock__brand">
                            <div class="dv2-dock__avatar" aria-hidden="true">
                                <img :src="logoSrc" alt="" />
                            </div>
                            <div>
                                <h2 class="dv2-dock__title">Seu assistente</h2>
                                <p class="dv2-dock__sub">Aqui para deixar seu dia mais simples.</p>
                            </div>
                        </div>
                        <div class="dv2-dock__head-actions">
                            <button
                                type="button"
                                class="dv2-dock__icon-btn"
                                title="Minimizar"
                                aria-label="Minimizar assistente"
                                @click="minimize"
                            >
                                <span aria-hidden="true">–</span>
                            </button>
                            <button
                                type="button"
                                class="dv2-dock__icon-btn"
                                title="Fechar"
                                aria-label="Fechar assistente"
                                @click="dismiss"
                            >
                                <span aria-hidden="true">×</span>
                            </button>
                        </div>
                    </header>

                    <div ref="messagesEl" class="dv2-dock__messages" aria-live="polite">
                        <div
                            v-for="msg in channel.messages"
                            :key="msg.id"
                            class="dv2-dock__row"
                            :class="msg.role === 'user' ? 'is-user' : 'is-bot'"
                        >
                            <div class="dv2-dock__bubble" :class="{ 'is-error': msg.isError }">
                                <div
                                    v-if="msg.role === 'assistant'"
                                    class="dv2-dock__html"
                                    v-html="renderMarkdown(msg.content)"
                                ></div>
                                <div v-else class="dv2-dock__text">{{ msg.content }}</div>

                                <div v-if="mediaActions(msg).length" class="dv2-dock__media">
                                    <button
                                        v-for="action in mediaActions(msg)"
                                        :key="`${msg.id}-m-${action.id}`"
                                        type="button"
                                        class="dv2-dock-media"
                                        @click="runAction(action)"
                                    >
                                        <img
                                            v-if="thumbFor(action)"
                                            :src="thumbFor(action)"
                                            :alt="action.label"
                                            loading="lazy"
                                        />
                                        <span>{{ action.label.replace(/^Assistir:\s*/i, '') }}</span>
                                    </button>
                                </div>

                                <div v-if="chipActions(msg).length" class="dv2-dock__actions">
                                    <button
                                        v-for="action in chipActions(msg)"
                                        :key="`${msg.id}-${action.id}`"
                                        type="button"
                                        class="dv2-dock-chip"
                                        @click="runAction(action)"
                                    >
                                        {{ action.label }}
                                    </button>
                                </div>

                                <div v-if="msg.sources?.length" class="dv2-dock__sources">
                                    <button
                                        v-for="source in msg.sources"
                                        :key="source.id"
                                        type="button"
                                        class="dv2-dock-chip"
                                        @click="goToSource(source)"
                                    >
                                        {{ source.title }}
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div v-if="channel.loading" class="dv2-dock__row is-bot" aria-hidden="true">
                            <div class="dv2-dock__bubble is-typing">
                                <span></span><span></span><span></span>
                            </div>
                        </div>
                    </div>

                    <form class="dv2-dock__composer" @submit.prevent="onSubmit">
                        <input
                            v-model="draft"
                            type="text"
                            maxlength="2000"
                            placeholder="Continue a conversa…"
                            aria-label="Mensagem para o assistente"
                            :disabled="channel.loading"
                        />
                        <button type="submit" class="dv2-dock__send" :disabled="!canSend" aria-label="Enviar">
                            ➤
                        </button>
                    </form>
                </section>
            </transition>

            <button
                v-show="!isOpen"
                type="button"
                class="dv2-dock__fab"
                :class="{ 'has-unread': unread }"
                aria-label="Abrir seu assistente"
                title="Seu assistente"
                @click="open"
            >
                <img :src="logoSrc" alt="" class="dv2-dock__fab-logo" />
                <span v-if="unread" class="dv2-dock__badge" aria-hidden="true"></span>
            </button>
        </div>
    </Teleport>
</template>

<script setup>
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useSupportAssistantStore } from '@/stores/supportAssistant';
import {
    sanitizeAssistantActions,
    isSafeAssistantPath,
    youtubeThumbnailUrl,
    openAssistantTutorialInNewTab,
} from '@/utils/dashboardAssistantActions';
import { renderSupportMarkdown } from '@/utils/supportMarkdown';
import logoMini from '@/assets/images/logo-mini.png';

const props = defineProps({
    /** Quando true (home /dashboard), o dock some — o painel embutido já está lá. */
    hideOnHome: { type: Boolean, default: true },
});

const route = useRoute();
const router = useRouter();
const assistantStore = useSupportAssistantStore();
const draft = ref('');
const messagesEl = ref(null);
const logoSrc = logoMini;

const channel = computed(() => assistantStore.channelState('dashboard'));
const isOpen = computed(() => !!assistantStore.dock.open);
const unread = computed(() => !!assistantStore.dock.unread);
const isDashboardHome = computed(
    () => route.name === 'dashboard' || route.path === '/dashboard'
);

const isDocumentoPreview = computed(
    () =>
        route.name === 'documento-preview' ||
        /^\/documento\/preview\//.test(route.path || '')
);

const visible = computed(() => {
    if (props.hideOnHome && isDashboardHome.value) return false;
    return assistantStore.shouldShowDashboardDock;
});

const canSend = computed(() => draft.value.trim().length >= 2 && !channel.value.loading);

function renderMarkdown(content) {
    return renderSupportMarkdown(content);
}

function safeActions(msg) {
    return sanitizeAssistantActions(msg?.actions);
}

function mediaActions(msg) {
    return safeActions(msg).filter((a) => a.id?.startsWith('tutorial_') && a.youtube_id);
}

function chipActions(msg) {
    return safeActions(msg).filter((a) => !(a.id?.startsWith('tutorial_') && a.youtube_id));
}

function thumbFor(action) {
    return youtubeThumbnailUrl(action?.youtube_id);
}

async function scrollToBottom() {
    await nextTick();
    if (messagesEl.value) {
        messagesEl.value.scrollTop = messagesEl.value.scrollHeight;
    }
}

watch(
    () => [channel.value.messages.length, channel.value.loading, isOpen.value],
    () => {
        if (isOpen.value) scrollToBottom();
    }
);

function open() {
    assistantStore.openDashboardDock();
}

function minimize() {
    assistantStore.minimizeDashboardDock();
}

function dismiss() {
    assistantStore.dismissDashboardDock();
}

async function onSubmit() {
    if (!canSend.value) return;
    const text = draft.value;
    draft.value = '';
    try {
        await assistantStore.sendMessage(text, { channel: 'dashboard' });
    } catch {
        // erro já na UI
    }
}

function runAction(action) {
    if (openAssistantTutorialInNewTab(action)) return;
    const to = String(action?.to || '').trim();
    if (!isSafeAssistantPath(to)) return;
    router.push(to);
}

function goToSource(source) {
    const routePath = String(source?.route || '').trim();
    if (isSafeAssistantPath(routePath)) {
        router.push(routePath);
        return;
    }
    router.push('/ajuda?tab=doc');
}

function onKeydown(event) {
    if (event.key === 'Escape' && isOpen.value) {
        minimize();
    }
}

onMounted(() => {
    window.addEventListener('keydown', onKeydown);
});

onUnmounted(() => {
    window.removeEventListener('keydown', onKeydown);
});
</script>

<style scoped>
.dv2-dock {
    position: fixed;
    right: 20px;
    bottom: 20px;
    z-index: 1200;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 12px;
    pointer-events: none;
}

.dv2-dock.is-documento-preview {
    bottom: 70px;
}

.dv2-dock__fab,
.dv2-dock__panel,
.dv2-dock__icon-btn,
.dv2-dock__send,
.dv2-dock-chip,
.dv2-dock-media {
    pointer-events: auto;
}

.dv2-dock__fab {
    width: 56px;
    height: 56px;
    border: 0;
    border-radius: 50%;
    background: var(--dv2-primary, #4361ee);
    box-shadow: 0 10px 28px rgba(15, 23, 42, 0.22);
    display: grid;
    place-items: center;
    cursor: pointer;
    position: relative;
}

.dv2-dock__fab:hover {
    transform: translateY(-1px);
}

.dv2-dock__fab-logo {
    width: 28px;
    height: 28px;
    object-fit: contain;
    filter: brightness(0) invert(1);
}

.dv2-dock__badge {
    position: absolute;
    top: 8px;
    right: 8px;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: #ef4444;
    border: 2px solid var(--dv2-surface, #fff);
}

.dv2-dock__panel {
    width: min(380px, calc(100vw - 24px));
    height: min(520px, calc(100vh - 100px));
    display: flex;
    flex-direction: column;
    background: var(--dv2-surface, #fff);
    border: 1px solid var(--dv2-border, #e2e8f0);
    border-radius: 18px;
    box-shadow: 0 18px 48px rgba(15, 23, 42, 0.18);
    overflow: hidden;
}

.dv2-dock__head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 10px;
    padding: 14px 14px 12px;
    border-bottom: 1px solid var(--dv2-border, #e2e8f0);
    background:
        linear-gradient(180deg, color-mix(in srgb, var(--dv2-primary, #4361ee) 8%, transparent), transparent);
}

.dv2-dock__brand {
    display: flex;
    gap: 10px;
    align-items: center;
    min-width: 0;
}

.dv2-dock__avatar {
    width: 36px;
    height: 36px;
    border-radius: 12px;
    display: grid;
    place-items: center;
    background: var(--dv2-primary-soft, #eef2ff);
    flex-shrink: 0;
}

.dv2-dock__avatar img {
    width: 70%;
    height: 70%;
    object-fit: contain;
}

.dv2-dock__title {
    margin: 0;
    font-size: 0.95rem;
    font-weight: 700;
    color: var(--dv2-navy, #0e1726);
}

.dv2-dock__sub {
    margin: 2px 0 0;
    font-size: 0.72rem;
    color: var(--dv2-muted, #64748b);
}

.dv2-dock__head-actions {
    display: flex;
    gap: 4px;
}

.dv2-dock__icon-btn {
    width: 30px;
    height: 30px;
    border: 0;
    border-radius: 8px;
    background: transparent;
    color: var(--dv2-muted, #64748b);
    font-size: 1.1rem;
    line-height: 1;
    cursor: pointer;
}

.dv2-dock__icon-btn:hover {
    background: var(--dv2-surface-2, #f8fafc);
    color: var(--dv2-navy, #0e1726);
}

.dv2-dock__messages {
    flex: 1;
    overflow-y: auto;
    padding: 12px;
    display: flex;
    flex-direction: column;
    gap: 10px;
    background: var(--dv2-surface-2, #f8fafc);
}

.dv2-dock__row {
    display: flex;
    max-width: 100%;
}

.dv2-dock__row.is-user {
    justify-content: flex-end;
}

.dv2-dock__bubble {
    max-width: 92%;
    padding: 10px 12px;
    border-radius: 14px;
    font-size: 0.86rem;
    line-height: 1.45;
    color: var(--dv2-navy, #0e1726);
    background: var(--dv2-surface, #fff);
    border: 1px solid var(--dv2-border, #e2e8f0);
}

.dv2-dock__row.is-user .dv2-dock__bubble {
    background: var(--dv2-primary, #4361ee);
    border-color: transparent;
    color: #fff;
}

.dv2-dock__html :deep(p) {
    margin: 0 0 0.45em;
}

.dv2-dock__html :deep(p:last-child) {
    margin-bottom: 0;
}

.dv2-dock__html :deep(ul),
.dv2-dock__html :deep(ol) {
    margin: 0.3em 0 0.45em;
    padding-left: 1.15em;
}

.dv2-dock__actions,
.dv2-dock__sources,
.dv2-dock__media {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 8px;
}

.dv2-dock-chip {
    border: 1px solid var(--dv2-border, #e2e8f0);
    background: var(--dv2-surface, #fff);
    color: var(--dv2-primary, #4361ee);
    border-radius: 999px;
    padding: 4px 10px;
    font-size: 0.72rem;
    font-weight: 650;
    cursor: pointer;
}

.dv2-dock-media {
    display: grid;
    grid-template-columns: 72px 1fr;
    gap: 8px;
    align-items: center;
    width: 100%;
    border: 1px solid var(--dv2-border, #e2e8f0);
    border-radius: 12px;
    overflow: hidden;
    background: var(--dv2-surface, #fff);
    padding: 0;
    text-align: left;
    cursor: pointer;
    color: var(--dv2-navy, #0e1726);
    font-size: 0.78rem;
    font-weight: 650;
}

.dv2-dock-media img {
    width: 72px;
    height: 48px;
    object-fit: cover;
}

.dv2-dock-media span {
    padding-right: 8px;
}

.dv2-dock__bubble.is-typing {
    display: inline-flex;
    gap: 5px;
    align-items: center;
    min-width: 52px;
}

.dv2-dock__bubble.is-typing span {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--dv2-muted, #64748b);
    animation: dv2-dock-bounce 1.1s infinite ease-in-out;
}

.dv2-dock__bubble.is-typing span:nth-child(2) {
    animation-delay: 0.15s;
}

.dv2-dock__bubble.is-typing span:nth-child(3) {
    animation-delay: 0.3s;
}

@keyframes dv2-dock-bounce {
    0%,
    80%,
    100% {
        transform: translateY(0);
        opacity: 0.45;
    }
    40% {
        transform: translateY(-3px);
        opacity: 1;
    }
}

.dv2-dock__composer {
    display: flex;
    gap: 8px;
    padding: 10px;
    border-top: 1px solid var(--dv2-border, #e2e8f0);
    background: var(--dv2-surface, #fff);
}

.dv2-dock__composer input {
    flex: 1;
    min-width: 0;
    border: 1px solid var(--dv2-border, #e2e8f0);
    border-radius: 12px;
    padding: 10px 12px;
    background: var(--dv2-surface, #fff);
    color: var(--dv2-navy, #0e1726);
    font-size: 0.88rem;
    outline: none;
}

.dv2-dock__send {
    width: 42px;
    height: 42px;
    border: 0;
    border-radius: 12px;
    background: var(--dv2-primary, #4361ee);
    color: #fff;
    cursor: pointer;
}

.dv2-dock__send:disabled {
    opacity: 0.45;
    cursor: not-allowed;
}

.dv2-dock-panel-enter-active,
.dv2-dock-panel-leave-active {
    transition: opacity 0.16s ease, transform 0.16s ease;
}

.dv2-dock-panel-enter-from,
.dv2-dock-panel-leave-to {
    opacity: 0;
    transform: translateY(8px) scale(0.98);
}

:global(body.dark) .dv2-dock__panel,
:global([data-bs-theme='dark']) .dv2-dock__panel,
:global(body.dark) .dv2-dock__composer,
:global([data-bs-theme='dark']) .dv2-dock__composer,
:global(body.dark) .dv2-dock__composer input,
:global([data-bs-theme='dark']) .dv2-dock__composer input,
:global(body.dark) .dv2-dock-chip,
:global([data-bs-theme='dark']) .dv2-dock-chip,
:global(body.dark) .dv2-dock-media,
:global([data-bs-theme='dark']) .dv2-dock-media {
    background: var(--dv2-surface, #0e1726);
    border-color: var(--dv2-border, #334155);
    color: var(--dv2-navy, #e0e6ed);
}

:global(body.dark) .dv2-dock__messages,
:global([data-bs-theme='dark']) .dv2-dock__messages {
    background: var(--dv2-surface-2, #1b2e4b);
}

:global(body.dark) .dv2-dock__row.is-bot .dv2-dock__bubble,
:global([data-bs-theme='dark']) .dv2-dock__row.is-bot .dv2-dock__bubble {
    background: var(--dv2-surface, #0e1726);
    border-color: var(--dv2-border, #334155);
    color: var(--dv2-navy, #e0e6ed);
}

@media (max-width: 640px) {
    .dv2-dock {
        right: 12px;
        bottom: 12px;
    }

    .dv2-dock.is-documento-preview {
        bottom: 70px;
    }

    .dv2-dock__panel {
        width: calc(100vw - 24px);
        height: min(70vh, 560px);
    }
}
</style>
